import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "../components/ui/toggle-group";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { MediaCard } from "../components/MediaCard";
import { ImportModal } from "../components/ImportModal";
import { LayoutGrid, List, BookOpen, Film, Tv, Gamepad2, Upload, GripVertical, Search, X, Star, CalendarDays, Layers } from "lucide-react";
import { motion } from "framer-motion";
import {
  DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, verticalListSortingStrategy, rectSortingStrategy, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "../i18n";

const API = '/api';

const MONTHS_ES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const MONTHS_EN = ["January","February","March","April","May","June","July","August","September","October","November","December"];

/* ─── Sortable Grid Card ─── */
function SortableGridCard({ entry, index }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: entry.entry_id });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 50 : undefined, opacity: isDragging ? 0.7 : 1, position: "relative" };
  return (
    <div ref={setNodeRef} style={style} data-testid={`sortable-grid-${entry.entry_id}`}>
      {entry.priority != null && (
        <div className="absolute top-1.5 left-1.5 z-20 bg-primary text-primary-foreground text-[10px] font-bold min-w-[22px] h-[22px] px-1 rounded-full flex items-center justify-center shadow-md" data-testid={`priority-badge-${entry.entry_id}`}>#{entry.priority}</div>
      )}
      <div {...attributes} {...listeners} className="absolute top-1.5 right-1.5 z-20 w-7 h-7 rounded-md bg-background/90 border border-border flex items-center justify-center cursor-grab active:cursor-grabbing touch-none shadow-sm" data-testid={`drag-handle-${entry.entry_id}`}>
        <GripVertical className="w-4 h-4 text-muted-foreground" />
      </div>
      <MediaCard entry={entry} showStatus index={index} />
    </div>
  );
}

/* ─── Sortable List Row ─── */
function SortableListRow({ entry }) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: entry.entry_id });
  const item = entry.item;
  const statusColors = { planned: "bg-gray-500", in_progress: "bg-blue-500", completed: "bg-green-500", dropped: "bg-red-500" };
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 50 : undefined, opacity: isDragging ? 0.7 : 1 };
  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2" data-testid={`sortable-list-${entry.entry_id}`}>
      <div {...attributes} {...listeners} className="flex-shrink-0 w-9 h-9 rounded-md border border-border flex items-center justify-center cursor-grab active:cursor-grabbing hover:bg-muted transition-colors touch-none" data-testid={`drag-handle-list-${entry.entry_id}`}>
        <GripVertical className="w-4 h-4 text-muted-foreground" />
      </div>
      <Link to={`/media/${item.item_id}`} className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card hover:bg-muted/50 transition-colors flex-1 min-w-0" data-testid={`library-list-item-${item.item_id}`}>
        {entry.priority != null && <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center flex-shrink-0" data-testid={`priority-badge-list-${entry.entry_id}`}>{entry.priority}</div>}
        <div className="w-11 h-14 flex-shrink-0 rounded overflow-hidden bg-muted">
          {item.cover_image || item.poster_url ? <img src={item.cover_image || item.poster_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">{item.title?.charAt(0)}</div>}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-medium truncate text-sm">{item.title}</h3>
          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5"><span className="font-mono uppercase">{t.mediaTypesShort[item.media_type] || item.media_type?.replace("_", " ")}</span>{item.creator && <><span>·</span><span className="truncate">{item.creator}</span></>}</div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">{entry.rating && <div className="text-sm font-mono">★ {entry.rating}</div>}<div className={`w-2 h-2 rounded-full ${statusColors[entry.status]}`} /></div>
      </Link>
    </div>
  );
}

/* ─── Plain List Row ─── */
function LibraryListItem({ entry, index }) {
  const { t } = useTranslation();
  const item = entry.item;
  const statusColors = { planned: "bg-gray-500", in_progress: "bg-blue-500", completed: "bg-green-500", dropped: "bg-red-500" };
  return (
    <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3, delay: index * 0.03 }}>
    <Link to={`/media/${item.item_id}`}
      className="flex items-center gap-4 p-3 rounded-lg border border-border bg-card hover:bg-muted/50 transition-colors" data-testid={`library-list-item-${item.item_id}`}>
      {entry.priority != null && entry.status === "planned" && <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center flex-shrink-0">{entry.priority}</div>}
      <div className="w-11 h-14 flex-shrink-0 rounded overflow-hidden bg-muted">
        {item.cover_image || item.poster_url ? <img src={item.cover_image || item.poster_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">{item.title?.charAt(0)}</div>}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-medium truncate text-sm">{item.title}</h3>
        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5"><span className="font-mono uppercase">{t.mediaTypesShort[item.media_type] || item.media_type?.replace("_", " ")}</span>{item.creator && <><span>·</span><span className="truncate">{item.creator}</span></>}</div>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">{entry.rating && <div className="text-sm font-mono">★ {entry.rating}</div>}<div className={`w-2 h-2 rounded-full ${statusColors[entry.status]}`} /></div>
    </Link>
    </motion.div>
  );
}

/* ─── Date Filter Popover ─── */
function DateFilterPopover({ dateMonth, dateYear, onApply, onClear, lang }) {
  const months = lang === "en" ? MONTHS_EN : MONTHS_ES;
  const [m, setM] = useState(dateMonth || "");
  const [y, setY] = useState(dateYear || "");
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - i);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors whitespace-nowrap ${dateMonth ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground border-border hover:border-foreground/30"}`} data-testid="filter-date-btn">
          <CalendarDays className="w-3 h-3" />
          {dateMonth ? `${months[parseInt(dateMonth) - 1]} ${dateYear}` : (lang === "en" ? "Date" : "Fecha")}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-3 space-y-3" align="start">
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">{lang === "en" ? "Month" : "Mes"}</p>
          <div className="grid grid-cols-3 gap-1">
            {months.map((name, i) => (
              <button key={i} onClick={() => setM(String(i + 1))} className={`text-xs py-1.5 px-1 rounded-md transition-colors ${m === String(i + 1) ? "bg-primary text-primary-foreground" : "hover:bg-muted text-foreground"}`}>{name.slice(0, 3)}</button>
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">{lang === "en" ? "Year" : "Año"}</p>
          <div className="grid grid-cols-5 gap-1">
            {years.map(yr => (
              <button key={yr} onClick={() => setY(String(yr))} className={`text-xs py-1.5 rounded-md transition-colors ${y === String(yr) ? "bg-primary text-primary-foreground" : "hover:bg-muted text-foreground"}`}>{yr}</button>
            ))}
          </div>
        </div>
        <div className="flex gap-2 pt-1">
          <Button size="sm" className="flex-1 h-7 text-xs" onClick={() => { if (m && y) onApply(m, y); }} disabled={!m || !y}>{lang === "en" ? "Apply" : "Aplicar"}</Button>
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onClear}>{lang === "en" ? "Clear" : "Limpiar"}</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/* ─── Main Library Page ─── */
export function Library({ user }) {
  const { t, lang } = useTranslation();
  const navigate = useNavigate();

  const categoryDefs = [
    { value: "all", label: t.library.allTypes, icon: Layers },
    { value: "book", label: t.mediaTypesPlural.book, icon: BookOpen },
    { value: "movie", label: t.mediaTypesPlural.movie, icon: Film },
    { value: "tv_series", label: t.mediaTypesPlural.tv_series, icon: Tv },
    { value: "video_game", label: t.mediaTypesPlural.video_game, icon: Gamepad2 },
  ];

  const sortOptions = [
    { value: "added_at", label: lang === "en" ? "Recently Added" : "Reciente" },
    { value: "title_az", label: lang === "en" ? "Title A-Z" : "Título A-Z" },
    { value: "rating", label: lang === "en" ? "Rating" : "Valoración" },
    { value: "release_year", label: lang === "en" ? "Year" : "Año" },
    { value: "priority", label: lang === "en" ? "Priority" : "Prioridad" },
  ];

  const [searchParams, setSearchParams] = useSearchParams();
  const [allEntries, setAllEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);

  // Restore state from sessionStorage on mount
  const saved = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem("lib_state") || "null"); } catch { return null; }
  }, []);

  const [viewMode, setViewMode] = useState(saved?.viewMode || "grid");
  const [searchQ, setSearchQ] = useState(saved?.searchQ || "");
  const [mediaType, setMediaType] = useState(saved?.mediaType || searchParams.get("type") || "all");
  const [activeStatuses, setActiveStatuses] = useState(() => new Set(saved?.activeStatuses || []));
  const [ratedOnly, setRatedOnly] = useState(saved?.ratedOnly || false);
  const [dateMonth, setDateMonth] = useState(saved?.dateMonth || "");
  const [dateYear, setDateYear] = useState(saved?.dateYear || "");
  const [sortBy, setSortBy] = useState(saved?.sortBy || "added_at");

  // Save filter state to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem("lib_state", JSON.stringify({
      searchQ, mediaType, activeStatuses: [...activeStatuses], ratedOnly, dateMonth, dateYear, sortBy, viewMode,
    }));
  }, [searchQ, mediaType, activeStatuses, ratedOnly, dateMonth, dateYear, sortBy, viewMode]);

  // Fetch all entries on mount
  const fetchLibrary = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/library?sort_by=added_at&sort_order=desc`);
      setAllEntries(response.data.entries || []);
    } catch { toast.error(t.library.loadError); }
    finally { setLoading(false); }
  }, [t]);

  useEffect(() => { fetchLibrary(); }, [fetchLibrary]);

  // Apply all filters client-side
  const filtered = useMemo(() => {
    let items = allEntries;

    // Search
    if (searchQ.trim()) {
      const q = searchQ.toLowerCase().trim();
      items = items.filter(e => e.item?.title?.toLowerCase().includes(q));
    }

    // Media type
    if (mediaType !== "all") {
      items = items.filter(e => e.item?.media_type === mediaType);
    }

    // Status multi-select
    if (activeStatuses.size > 0) {
      items = items.filter(e => activeStatuses.has(e.status));
    }

    // Rated only
    if (ratedOnly) {
      items = items.filter(e => e.rating && e.rating > 0);
    }

    // Date filter (completion date)
    if (dateMonth && dateYear) {
      items = items.filter(e => {
        const d = e.updated_at || e.added_at;
        if (!d || e.status !== "completed") return false;
        const date = new Date(d);
        return date.getMonth() + 1 === parseInt(dateMonth) && date.getFullYear() === parseInt(dateYear);
      });
    }

    // Sort
    const sorted = [...items];
    if (sortBy === "title_az") {
      sorted.sort((a, b) => (a.item?.title || "").localeCompare(b.item?.title || ""));
    } else if (sortBy === "rating") {
      sorted.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    } else if (sortBy === "release_year") {
      sorted.sort((a, b) => (b.item?.release_year || 0) - (a.item?.release_year || 0));
    } else if (sortBy === "priority") {
      sorted.sort((a, b) => {
        const ap = a.priority ?? 9999;
        const bp = b.priority ?? 9999;
        return ap - bp;
      });
    } else {
      sorted.sort((a, b) => new Date(b.added_at || 0) - new Date(a.added_at || 0));
    }

    return sorted;
  }, [allEntries, searchQ, mediaType, activeStatuses, ratedOnly, dateMonth, dateYear, sortBy]);

  // Category counts
  const categoryCounts = useMemo(() => {
    let base = allEntries;
    if (searchQ.trim()) {
      const q = searchQ.toLowerCase().trim();
      base = base.filter(e => e.item?.title?.toLowerCase().includes(q));
    }
    if (activeStatuses.size > 0) base = base.filter(e => activeStatuses.has(e.status));
    if (ratedOnly) base = base.filter(e => e.rating && e.rating > 0);
    if (dateMonth && dateYear) base = base.filter(e => { const d = e.updated_at || e.added_at; if (!d || e.status !== "completed") return false; const date = new Date(d); return date.getMonth() + 1 === parseInt(dateMonth) && date.getFullYear() === parseInt(dateYear); });

    const counts = { all: base.length };
    for (const e of base) { const mt = e.item?.media_type; if (mt) counts[mt] = (counts[mt] || 0) + 1; }
    return counts;
  }, [allEntries, searchQ, activeStatuses, ratedOnly, dateMonth, dateYear]);

  // Status toggle
  const toggleStatus = (s) => {
    setActiveStatuses(prev => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s); else next.add(s);
      return next;
    });
  };

  // Clear all filters
  const clearFilters = () => { setSearchQ(""); setMediaType("all"); setActiveStatuses(new Set()); setRatedOnly(false); setDateMonth(""); setDateYear(""); setSortBy("added_at"); };
  const hasAnyFilter = searchQ || mediaType !== "all" || activeStatuses.size > 0 || ratedOnly || dateMonth;

  // Active filter tags
  const activeTags = useMemo(() => {
    const tags = [];
    if (mediaType !== "all") {
      const def = categoryDefs.find(c => c.value === mediaType);
      tags.push({ key: "type", label: def?.label || mediaType, onRemove: () => setMediaType("all") });
    }
    const statusLabels = { planned: t.statuses.planned, in_progress: t.statuses.in_progress, completed: t.statuses.completed, dropped: t.statuses.dropped };
    for (const s of activeStatuses) {
      tags.push({ key: `status-${s}`, label: statusLabels[s] || s, onRemove: () => toggleStatus(s) });
    }
    if (ratedOnly) tags.push({ key: "rated", label: lang === "en" ? "Rated" : "Valorados", onRemove: () => setRatedOnly(false) });
    if (dateMonth && dateYear) {
      const months = lang === "en" ? MONTHS_EN : MONTHS_ES;
      tags.push({ key: "date", label: `${months[parseInt(dateMonth) - 1]} ${dateYear}`, onRemove: () => { setDateMonth(""); setDateYear(""); } });
    }
    return tags;
  }, [mediaType, activeStatuses, ratedOnly, dateMonth, dateYear, t, lang, categoryDefs]);

  // Drag & drop
  const isDraggable = activeStatuses.has("planned") && activeStatuses.size === 1 && sortBy === "priority";
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );
  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const draggedEntry = filtered.find(e => e.entry_id === active.id);
    if (!draggedEntry) return;
    const draggedType = draggedEntry.item?.media_type;
    const sameType = filtered.filter(e => e.item?.media_type === draggedType);
    const oldIdx = sameType.findIndex(e => e.entry_id === active.id);
    const newIdx = sameType.findIndex(e => e.entry_id === over.id);
    if (newIdx === -1) return;
    const reordered = arrayMove(sameType, oldIdx, newIdx).map((e, i) => ({ ...e, priority: i + 1 }));
    const updatedMap = new Map(reordered.map(e => [e.entry_id, e]));
    setAllEntries(prev => prev.map(e => updatedMap.get(e.entry_id) || e));
    try {
      await axios.post(`${API}/library/reorder`, { ordered_entry_ids: reordered.map(e => e.entry_id), media_type: draggedType });
    } catch { toast.error("Error reordering"); fetchLibrary(); }
  }, [filtered, fetchLibrary]);

  // Status pill definitions
  const statusDefs = [
    { key: "planned", label: t.statuses.planned, color: "border-gray-400 text-gray-600 dark:text-gray-400" },
    { key: "in_progress", label: t.statuses.in_progress, color: "border-blue-400 text-blue-600 dark:text-blue-400" },
    { key: "completed", label: t.statuses.completed, color: "border-green-400 text-green-600 dark:text-green-400" },
    { key: "dropped", label: t.statuses.dropped, color: "border-red-400 text-red-600 dark:text-red-400" },
  ];

  return (
    <div className="space-y-4 md:space-y-6 animate-fade-in" data-testid="library-page">
      {/* ═══ Header (UNCHANGED) ═══ */}
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{t.library.title}</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setImportOpen(true)} className="min-h-[44px]" data-testid="import-library-btn">
            <Upload className="w-4 h-4 mr-2" />{t.importModal.importButton}
          </Button>
          <Button variant="outline" onClick={() => navigate("/search")} className="min-h-[44px]" data-testid="add-item-btn">
            <Search className="w-4 h-4 mr-2" />{t.library.addTitle}
          </Button>
        </div>
      </div>

      {/* ═══ FILTER BAR (REDESIGNED) ═══ */}
      <div className="space-y-2.5 bg-card border border-border rounded-xl p-3 md:p-4" data-testid="library-filter-bar">

        {/* Row 1: Search + Sort + View toggle */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              placeholder={lang === "en" ? "Search your library..." : "Busca en tu biblioteca..."}
              className="w-full h-10 pl-9 pr-3 rounded-lg border border-border bg-background text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
              data-testid="library-search-input"
            />
          </div>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-auto min-w-[110px] h-10 text-xs flex-shrink-0" data-testid="filter-sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sortOptions.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <ToggleGroup type="single" value={viewMode} onValueChange={v => v && setViewMode(v)} className="bg-muted rounded-lg p-0.5 flex-shrink-0">
            <ToggleGroupItem value="grid" aria-label="Grid" className="w-10 h-10" data-testid="view-grid"><LayoutGrid className="w-4 h-4" /></ToggleGroupItem>
            <ToggleGroupItem value="list" aria-label="List" className="w-10 h-10" data-testid="view-list"><List className="w-4 h-4" /></ToggleGroupItem>
          </ToggleGroup>
        </div>

        {/* Row 2: Category pills (horizontal scroll on mobile) */}
        <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide pb-0.5 -mx-1 px-1" data-testid="category-pills">
          {categoryDefs.map(cat => {
            const isActive = mediaType === cat.value;
            const count = categoryCounts[cat.value] ?? 0;
            const Icon = cat.icon;
            return (
              <button
                key={cat.value}
                onClick={() => setMediaType(cat.value)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all whitespace-nowrap flex-shrink-0 ${
                  isActive ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground border-border hover:border-foreground/30 hover:text-foreground"
                }`}
                data-testid={`filter-type-${cat.value}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {cat.label}
                {isActive && <span className="ml-0.5 bg-primary-foreground/20 text-primary-foreground text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">{count}</span>}
              </button>
            );
          })}
        </div>

        {/* Row 3: Quick status filters + Rated + Date */}
        <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide pb-0.5 -mx-1 px-1" data-testid="quick-filters">
          {statusDefs.map(sd => {
            const isActive = activeStatuses.has(sd.key);
            return (
              <button
                key={sd.key}
                onClick={() => toggleStatus(sd.key)}
                className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-medium border transition-all whitespace-nowrap flex-shrink-0 ${
                  isActive ? `bg-primary text-primary-foreground border-primary` : `bg-background ${sd.color} border-border hover:border-foreground/30`
                }`}
                data-testid={`filter-status-${sd.key}`}
              >
                {sd.label}
              </button>
            );
          })}
          <button
            onClick={() => setRatedOnly(!ratedOnly)}
            className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-medium border transition-all whitespace-nowrap flex-shrink-0 ${
              ratedOnly ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground border-border hover:border-foreground/30"
            }`}
            data-testid="filter-rated"
          >
            <Star className="w-3 h-3" />
            {lang === "en" ? "Rated" : "Valorados"}
          </button>
          <DateFilterPopover
            dateMonth={dateMonth}
            dateYear={dateYear}
            onApply={(m, y) => { setDateMonth(m); setDateYear(y); }}
            onClear={() => { setDateMonth(""); setDateYear(""); }}
            lang={lang}
          />
        </div>

        {/* Row 4: Summary + active tags */}
        <div className="flex items-center gap-2 flex-wrap pt-0.5">
          <p className="text-xs text-muted-foreground" data-testid="results-summary">
            {hasAnyFilter
              ? (lang === "en" ? `Showing ${filtered.length} titles` : `Mostrando ${filtered.length} títulos`)
              : (lang === "en" ? `${filtered.length} titles in your collection` : `${filtered.length} títulos en tu colección`)}
          </p>
          {activeTags.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap ml-auto">
              {activeTags.map(tag => (
                <button
                  key={tag.key}
                  onClick={tag.onRemove}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-muted text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
                  data-testid={`filter-tag-${tag.key}`}
                >
                  {tag.label}
                  <X className="w-2.5 h-2.5" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Drag hint */}
        {isDraggable && filtered.length > 1 && (
          <div className="text-xs text-muted-foreground bg-muted/50 rounded-lg px-3 py-2 flex items-center gap-2" data-testid="drag-hint">
            <GripVertical className="w-3.5 h-3.5" />
            {t.library.dragHint}
          </div>
        )}
      </div>

      {/* ═══ Content (rendering logic UNCHANGED) ═══ */}
      {loading ? (
        <div className="flex items-center justify-center min-h-[30vh]"><div className="animate-pulse text-muted-foreground">{t.library.loadingLibrary}</div></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 md:py-16" data-testid="library-empty">
          <BookOpen className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-base md:text-lg font-semibold mb-2">
            {hasAnyFilter ? (lang === "en" ? "No titles match these filters" : "Ningún título coincide con estos filtros") : t.library.emptyLibrary}
          </h3>
          <p className="text-sm text-muted-foreground mb-4 md:mb-6">
            {hasAnyFilter ? (lang === "en" ? "Try adjusting your filters" : "Intenta ajustar los filtros") : t.library.startSearching}
          </p>
          {hasAnyFilter && <Button variant="outline" onClick={clearFilters} className="min-h-[44px]" data-testid="clear-filters">{t.library.clearFilters}</Button>}
        </div>
      ) : isDraggable ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={filtered.map(e => e.entry_id)} strategy={viewMode === "grid" ? rectSortingStrategy : verticalListSortingStrategy}>
            {viewMode === "grid" ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6" data-testid="library-grid">
                {filtered.map((entry, index) => <SortableGridCard key={entry.entry_id} entry={entry} index={index} />)}
              </div>
            ) : (
              <div className="space-y-2" data-testid="library-list">
                {filtered.map((entry, index) => <SortableListRow key={entry.entry_id} entry={entry} index={index} />)}
              </div>
            )}
          </SortableContext>
        </DndContext>
      ) : viewMode === "grid" ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6" data-testid="library-grid">
          {filtered.map((entry, index) => <MediaCard key={entry.entry_id} entry={entry} showStatus index={index} />)}
        </div>
      ) : (
        <div className="space-y-2" data-testid="library-list">
          {filtered.map((entry, index) => <LibraryListItem key={entry.entry_id} entry={entry} index={index} />)}
        </div>
      )}

      <ImportModal isOpen={importOpen} onClose={() => setImportOpen(false)} onSuccess={fetchLibrary} />
    </div>
  );
}

export default Library;
